import { useEffect, useState } from "react";
import { DEFAULT_PAGE_SIZE, PERMISSIONS } from "../../configs/constants";
import {
  getPagedItems,
  getComprehensiveMerchantItems,
  getHeaderCounterText,
} from "../../utils/common";
import Preference, { ContentOption } from "../UI/Preference";
import Pagination from "../UI/Pagination";
import {
  Box,
  SpaceBetween,
  Header,
  Checkbox,
  Link,
  ColumnLayout,
  Container,
  FormField,
  RadioGroup,
  Select,
  Table as CSDTable,
  TableProps,
  Button,
  ButtonProps,
  Grid,
  Spinner,
} from "@cloudscape-design/components-themed";
import {
  COMMON_LABELS,
  DivisionDict,
  addBlank,
  getDivisionLabel,
} from "../default-constants";
import {
  CONTRACT_COMPREHENSIVE,
  CONTRACT_ROUTE_SELECT_ITEMS,
  LABELS,
} from "./constants";
import InfoModal from "./InfoModal";
import { formatDate } from "../../utils/format";
import { useHasAuthorityHook } from "../../app/useHasAuthorityHook";
import {
  useGetComprehensiveCompaniesQuery,
  useGetGwCompaniesQuery,
  useGetMerchantInfoInquiryMerchantQuery,
  useGetMerchantStoreSiteIdQuery,
  useGetMerchantStoreStoreNameQuery,
  useLazyGetMerchantMerchantsDownloadQuery,
} from "../../features/api";
import { MerchantInfoInquiryMerchant } from "../../features/api-interfaces";
import { useAppDispatch, useAppSelector } from "../../app/hook";
import { createSearchCondition } from "../../features/slices/merchant-tab-search";
import { getMerchantStoreNameItems, getMerchantStoreSiteIdItems } from "./util";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import { IconButton } from "../UI/button/IconButton";
import { showModal } from "../UI/modal/modal-slice";

interface MerchantProps {
  companyCode: string;
  isDocomoUser: boolean;
}

export const MerchantTab = (props: MerchantProps) => {
  const { companyCode, isDocomoUser } = props;
  const dispatch = useAppDispatch();
  const isRateReadUser = useHasAuthorityHook(PERMISSIONS.RATE_READ);
  const isDocomoController = useHasAuthorityHook(
    PERMISSIONS.APPLY_MERCHANT_DIRECT
  );
  // GW一覧データの取得
  const { data: gw } = useGetGwCompaniesQuery(undefined, {
    skip: !isDocomoUser,
  });
  // GW一覧データの加工(viewから取得しているため決済GW事業者コードをキーに重複を削除)
  const uniqueData = gw
    ? Array.from(
        new Map(
          gw.map((item) => [item.gatewayNo?.toString() ?? "", item])
        ).values()
      )
    : [];

  const gwListData = uniqueData
    ? addBlank(
        uniqueData.map((item) => ({
          label: item.gwName,
          value: item.gatewayNo?.toString() ?? "",
        }))
      )
    : [];

  const searchCondition = useAppSelector(
    (state) => state.merchantInfoInquiryMerchantTabSearchCondition
  );
  const { data: merchantData, isFetching } =
    useGetMerchantInfoInquiryMerchantQuery({
      companyCode,
      search: searchCondition,
    });
  const { data: comprehensiveCompanies } = useGetComprehensiveCompaniesQuery({
    companyCode: "",
    invisibleComprehensiveCompanyNoList: [],
  });
  const comprehensiveMerchantListItems = addBlank(
    getComprehensiveMerchantItems(comprehensiveCompanies)
  );
  const { data: merchantStoreStoreName } =
    useGetMerchantStoreStoreNameQuery(companyCode);
  const { data: merchantStoreSiteId } =
    useGetMerchantStoreSiteIdQuery(companyCode);
  const columnDefinitionsBase: TableProps.ColumnDefinition<MerchantInfoInquiryMerchant>[] =
    [
      {
        id: "createdAt",
        header: LABELS.CREATE_DATE,
        cell: (item) => formatDate(item.createdAt),
      },
      {
        id: "contractStatus",
        header: LABELS.STATUS,
        cell: (item) => item.contractStatus ?? "　",
      },
      {
        id: "contractServiceCode",
        header: LABELS.SERVICE,
        cell: (item) => item.contractServiceCode ?? "　",
      },
      {
        id: "contractCompanyName",
        header: LABELS.CONTRACT_ROUTE,
        cell: (item) => item.contractCompanyName ?? "　",
      },
      {
        id: "tradeName",
        header: LABELS.MERCHANT_NAME,
        cell: (item) => item.tradeName ?? "　",
      },
      {
        id: "payingType",
        header: LABELS.CHARGE,
        cell: (item) => item.payingType ?? "　",
      },
      {
        id: "qrPaymentMethod",
        header: LABELS.PAYMENT_METHOD,
        cell: (item) => item.qrPresentationCategory ?? "　",
      },
      {
        id: "siteId",
        header: LABELS.SITE_ID,
        cell: (item) => (
          // 加盟店情報照会（加盟店詳細）のポップアップ表示
          <Link
            onFollow={() => {
              setSiteId(item.siteId);
              setVisible(true);
            }}
          >
            {item.siteId}
          </Link>
        ),
      },
    ];
  const columnDefinitionsBaseNotGW: TableProps.ColumnDefinition<MerchantInfoInquiryMerchant>[] =
    [
      {
        id: "createdAt",
        header: LABELS.CREATE_DATE,
        cell: (item) => formatDate(item.createdAt),
      },
      {
        id: "contractStatus",
        header: LABELS.STATUS,
        cell: (item) => item.contractStatus ?? "　",
      },
      {
        id: "contractServiceCode",
        header: LABELS.SERVICE,
        cell: (item) => item.contractServiceCode ?? "　",
      },
      {
        id: "contractCompanyName",
        header: LABELS.CONTRACT_ROUTE,
        cell: (item) => item.contractCompanyName ?? "　",
      },
      {
        id: "tradeName",
        header: LABELS.MERCHANT_NAME,
        cell: (item) => item.tradeName ?? "　",
      },
      {
        id: "payingType",
        header: LABELS.CHARGE,
        cell: (item) => item.payingType ?? "　",
      },
      {
        id: "qrPaymentMethod",
        header: LABELS.PAYMENT_METHOD,
        cell: (item) => item.qrPresentationCategory ?? "　",
      },
      {
        id: "siteId",
        header: LABELS.SITE_ID,

        cell: (item) => (
          // 加盟店情報照会（加盟店詳細）のポップアップ表示
          <Link
            onFollow={() => {
              setSiteId(item.siteId);
              setVisible(true);
            }}
          >
            {item.siteId}
          </Link>
        ),
      },
      {
        id: "rate",
        header: LABELS.RATE,
        cell: (item) => item.rate,
      },
    ];
  const columnDefinitions: TableProps.ColumnDefinition<MerchantInfoInquiryMerchant>[] =
    [
      {
        id: "createdAt",
        header: LABELS.CREATE_DATE,
        cell: (item) => formatDate(item.createdAt),
      },
      {
        id: "contractStatus",
        header: LABELS.STATUS,
        cell: (item) => item.contractStatus ?? "　",
      },
      {
        id: "contractServiceCode",
        header: LABELS.SERVICE,
        cell: (item) => item.contractServiceCode ?? "　",
      },
      {
        id: "contractCompanyName",
        header: LABELS.CONTRACT_ROUTE,
        cell: (item) => item.contractCompanyName ?? "　",
      },
      {
        id: "tradeName",
        header: LABELS.MERCHANT_NAME,
        cell: (item) => item.tradeName ?? "　",
      },
      {
        id: "payingType",
        header: LABELS.CHARGE,
        cell: (item) => item.payingType ?? "　",
      },
      {
        id: "qrPaymentMethod",
        header: LABELS.PAYMENT_METHOD,
        cell: (item) => item.qrPresentationCategory ?? "　",
      },
      {
        id: "siteId",
        header: LABELS.SITE_ID,

        cell: (item) => (
          // 加盟店情報照会（加盟店詳細）のポップアップ表示
          <Link
            onFollow={() => {
              setSiteId(item.siteId);
              setVisible(true);
            }}
          >
            {item.siteId}
          </Link>
        ),
      },
      // Orange項目追加
      //  決済グループID
      {
        id: "settlementGroupId",
        header: LABELS.SETTLEMENT_GROUP_ID,
        cell: (item) => item.settlementGroupId ?? "　",
      },
      //  Access Key
      {
        id: "accessKey",
        header: LABELS.ACCESS_KEY,
        cell: (item) => item.accessKey ?? "　",
      },
      //  Secret Key
      {
        id: "secretKey",
        header: LABELS.SECRET_KEY,
        cell: (item) => item.secretKey ?? "　",
      },
      {
        id: "shopId",
        header: LABELS.SHOPID,
        cell: (item) => item.shopId ?? "　",
      },
      {
        id: "terminalIdentificationNumber",
        header: LABELS.TERMINAL_IDENTIFICATION_NUMBER,
        cell: (item) => item.terminalIdentificationNumber,
      },
      {
        id: "rate",
        header: LABELS.RATE,
        cell: (item) => item.rate,
      },
    ];
  const columnItemBase =
    isRateReadUser && isDocomoController
      ? columnDefinitions
      : isRateReadUser
      ? columnDefinitionsBaseNotGW
      : columnDefinitionsBase;
  const options: ContentOption[] = columnItemBase.map(
    (column) =>
      ({
        id: column.id!,
        label: column.header,
      } as ContentOption)
  );
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [preferences, setPreferences] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    visibleContent: options.map((option) => option.id),
  });
  const [pagedItems, setPagedItems] = useState<MerchantInfoInquiryMerchant[]>(
    []
  );
  const [allItems, setAllItems] = useState<MerchantInfoInquiryMerchant[]>([]);
  const [visible, setVisible] = useState(false);
  const [siteId, setSiteId] = useState("");
  const [comprehensiveCompaniesList, setComprehensiveCompaniesList] = useState<
    DivisionDict[]
  >([]);
  const [merchantStoreStoreNameList, setMerchantStoreStoreNameList] = useState<
    DivisionDict[]
  >([]);
  const [merchantStoreSiteIdList, setMerchantStoreSiteIdList] = useState<
    DivisionDict[]
  >([]);
  const [searchInput, setSearchInput] = useState({
    contractRoute: "",
    contractRouteName: "",
    merchantName: "",
    siteId: "",
    gwCompanyNo: "",
    isGetVerificationSiteId: false,
  });
  const [isInclusionMerchant, setIsInclusionMerchant] = useState(false);
  const [disabledInclusionMerchant, setDisabledInclusionMerchant] =
    useState(false);

  // spinnerと文字列表示有無
  const [isDownloadSpinnerAndStrDisplay, setIsDownloadSpinnerAndStrDisplay] =
    useState<boolean>(false);
  // button表示有無
  const [
    isMerchantMerchantsDownloadButton,
    setIsMerchantMerchantsDownloadButton,
  ] = useState<boolean>(false);

  useEffect(() => {
    setComprehensiveCompaniesList(comprehensiveMerchantListItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comprehensiveCompanies]);

  useEffect(() => {
    setMerchantStoreStoreNameList(
      addBlank(getMerchantStoreNameItems(merchantStoreStoreName))
    );
  }, [merchantStoreStoreName]);

  useEffect(() => {
    setMerchantStoreSiteIdList(
      addBlank(getMerchantStoreSiteIdItems(merchantStoreSiteId))
    );
  }, [merchantStoreSiteId]);

  /** 設定変更関数 */
  const handleOnConfirm = (detail: any) => {
    const newPageSize = detail.pageSize;
    setPreferences({
      pageSize: newPageSize,
      visibleContent: detail.visibleContent,
    });
    if (merchantData) {
      const uncheckedItems = merchantData.filter(
        (merchant) => !merchant.verificationCpidFlag
      );
      setPagedItems(
        isInclusionMerchant
          ? merchantData.slice(0, newPageSize)
          : uncheckedItems
      );
      setCurrentPageIndex(1);
    }
  };

  //モーダル閉じる関数
  const closeDetailModal = () => {
    setVisible(false);
  };

  /**
   * 検索ボタンを押した時の処理.
   * @param {CustomEvent<ButtonProps.ClickDetail>} event
   */
  const onSearch = (event: CustomEvent<ButtonProps.ClickDetail>) => {
    event.preventDefault();
    dispatch(
      createSearchCondition({ ...searchInput, timestamp: new Date().getTime() })
    );
  };

  useEffect(() => {
    if (merchantData !== undefined) {
      const defaultMerchants = isInclusionMerchant
        ? merchantData
        : merchantData.filter((merchant) => !merchant.verificationCpidFlag);
      setPagedItems(
        getPagedItems(
          defaultMerchants,
          1,
          preferences.pageSize
        ) as MerchantInfoInquiryMerchant[]
      );
      setAllItems(defaultMerchants);
      setDisabledInclusionMerchant(
        merchantData.every((merchant) => !merchant.verificationCpidFlag)
      );
      setCurrentPageIndex(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantData]);

  // ダウンロード関連変数
  const [lazyDownloadMerchantMerchantsTrigger] =
    useLazyGetMerchantMerchantsDownloadQuery();

  const onClickMerchantDownloadButton = (e: CustomEvent<ClickDetail>) => {
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: COMMON_LABELS.CONTENTS.DL_WAIT,
        positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
        negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
        positiveFn: () => {
          e.preventDefault();
          executeDownload();
        },
      })
    );
  };

  const executeDownload = async () => {
    // spinner・文字列活性
    setIsDownloadSpinnerAndStrDisplay(true);
    // button活性
    setIsMerchantMerchantsDownloadButton(true);
    await lazyDownloadMerchantMerchantsTrigger({
      companyCode: companyCode,
      search: { ...searchInput, timestamp: new Date().getTime() },
    })
      .unwrap()
      .then()
      .finally(() => {
        // spinner・文字列非活性
        setIsDownloadSpinnerAndStrDisplay(false);
        // button非活性
        setIsMerchantMerchantsDownloadButton(false);
      });
  };

  /**
   * 「検証サイトIDを表示する」のチェックボックス押下時に加盟店一覧のデータ表示を切り替える
   * @param {boolean} checked チェックボックスの状態
   */
  const changeMerchantsByVerificationCpidFlag = (checked: boolean) => {
    const uncheckedItems = merchantData!.filter(
      (item) => !item.verificationCpidFlag
    );
    const newPageItems = checked ? merchantData! : uncheckedItems;
    setAllItems(newPageItems);
    setPagedItems(newPageItems.slice(0, preferences.pageSize));
    setCurrentPageIndex(1);
    setIsInclusionMerchant(!isInclusionMerchant);
  };

  return (
    <SpaceBetween direction={"vertical"} size={"m"}>
      <Container>
        <SpaceBetween direction={"vertical"} size={"l"}>
          <ColumnLayout columns={4}>
            {isRateReadUser && (
              <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
                <FormField label={LABELS.CONTRACT_ROUTE}>
                  <RadioGroup
                    items={CONTRACT_ROUTE_SELECT_ITEMS}
                    value={searchInput.contractRoute}
                    onChange={({ detail }) =>
                      setSearchInput({
                        ...searchInput,
                        contractRoute: detail.value,
                        contractRouteName: "",
                      })
                    }
                  />
                </FormField>
                <FormField
                  label={LABELS.BLANK}
                  constraintText="　　　　　　　　　　　　　"
                >
                  <Select
                    selectedOption={{ value: searchInput.contractRouteName }}
                    options={comprehensiveCompaniesList}
                    onChange={({ detail }) =>
                      setSearchInput({
                        ...searchInput,
                        contractRouteName: detail.selectedOption
                          .value as string,
                      })
                    }
                    filteringType={"auto"}
                    disabled={
                      searchInput.contractRoute !== CONTRACT_COMPREHENSIVE
                    }
                  />
                </FormField>
              </Grid>
            )}
            <FormField label={LABELS.MERCHANT_NAME}>
              <Select
                selectedOption={{ value: searchInput.merchantName }}
                options={merchantStoreStoreNameList}
                onChange={({ detail }) =>
                  setSearchInput({
                    ...searchInput,
                    merchantName: detail.selectedOption.value as string,
                  })
                }
                filteringType={"auto"}
              />
            </FormField>
            <FormField label={LABELS.SITE_ID}>
              <Select
                selectedOption={{ value: searchInput.siteId }}
                options={merchantStoreSiteIdList}
                onChange={({ detail }) =>
                  setSearchInput({
                    ...searchInput,
                    siteId: detail.selectedOption.value as string,
                  })
                }
                filteringType={"auto"}
              />
            </FormField>
            {isDocomoController && (
              <FormField
                label={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Box variant="strong">{LABELS.GW_COMPANY}</Box>
                  </SpaceBetween>
                }
                stretch={true}
              >
                <Select
                  selectedOption={{
                    value: searchInput.gwCompanyNo,
                    label: getDivisionLabel(
                      gwListData,
                      searchInput.gwCompanyNo
                    ),
                  }}
                  filteringType="auto"
                  onChange={({ detail: { selectedOption } }) => {
                    setSearchInput({
                      ...searchInput,
                      gwCompanyNo: selectedOption.value!,
                    });
                  }}
                  options={gwListData}
                  selectedAriaLabel="Selected"
                />
              </FormField>
            )}
          </ColumnLayout>
        </SpaceBetween>
      </Container>
      <ColumnLayout columns={2}>
        <Box float={"left"}>
          <Checkbox
            onChange={({ detail }) => {
              setSearchInput({
                ...searchInput,
                isGetVerificationSiteId: detail.checked as boolean,
              });
              changeMerchantsByVerificationCpidFlag(detail.checked);
            }}
            checked={isInclusionMerchant}
            disabled={disabledInclusionMerchant}
          >
            {LABELS.MERCHANT_CHECKBOX}
          </Checkbox>
        </Box>
        <Box float={"right"}>
          <Button formAction={"none"} variant={"normal"} onClick={onSearch}>
            {COMMON_LABELS.BTNS.SEARCH}
          </Button>
        </Box>
      </ColumnLayout>
      <CSDTable
        columnDefinitions={columnItemBase}
        loading={isFetching}
        loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
        trackBy="id"
        items={pagedItems}
        resizableColumns
        stickyHeader
        header={
          <Header
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {isDownloadSpinnerAndStrDisplay ? (
                  <Spinner size="big" />
                ) : (
                  <></>
                )}
                {isDownloadSpinnerAndStrDisplay ? (
                  <>{COMMON_LABELS.CONTENTS.DL_RUN}</>
                ) : (
                  <></>
                )}
                <IconButton
                  iconName={"download"}
                  label={COMMON_LABELS.BTNS.DL}
                  disabled={isMerchantMerchantsDownloadButton}
                  onClick={onClickMerchantDownloadButton}
                />
              </SpaceBetween>
            }
          >
            {LABELS.MERCHANT_TABLE}
            {getHeaderCounterText(allItems.length, pagedItems.length)}
          </Header>
        }
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            filteredItems={allItems}
            setCurrentPageIndex={setCurrentPageIndex}
            pageSize={preferences.pageSize}
            setPagedItems={setPagedItems}
          />
        }
        preferences={
          <Preference
            preferences={preferences}
            options={options}
            handleOnConfirm={handleOnConfirm}
          />
        }
        empty={
          <Box textAlign="center" color="inherit">
            {COMMON_LABELS.CSD.NO_DATA}
          </Box>
        }
      />
      {/* <ColumnLayout columns={2}>
        <Box float="left">
          <Button
            variant="normal"
            iconName="download"
            iconAlign="right"
            onClick={onClickDownloadButton}
          >
            {COMMON_LABELS.BTNS.DL}
          </Button>
        </Box>

        <Box float="right">
          <SpaceBetween direction="horizontal" size="xxl">
            <Button onClick={onClickRegisterFormButton} variant="normal">
              {"　料率変更　"}
            </Button>
            <Button onClick={onClickRegisterFormButton} variant="normal">
              {"加盟店情報変更"}
            </Button>
          </SpaceBetween>
        </Box>
      </ColumnLayout> */}
      {siteId && (
        <InfoModal
          visible={visible}
          closeDetailModal={closeDetailModal}
          propsCompanyCode={companyCode}
          propsSiteId={siteId}
        />
      )}
    </SpaceBetween>
  );
};

export default MerchantTab;
